import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Utilities } from '../../utilities';
import { eAssetType, eDatabaseType, eLogSeverity, eSystemEventType } from '../../enums';

/** Numeric enums: the API may send the number, translations are keyed by member name. */
const NUMERIC_ENUMS: Record<string, Record<string | number, string | number>> = {
  assetType: eAssetType,
  databaseType: eDatabaseType,
  logSeverity: eLogSeverity,
  systemEventType: eSystemEventType,
};

/**
 * Translates enum values (and enumerated types returned by the API) using the
 * `enums.<enumName>.<value>` keys in the reg-hub-common translation files.
 *
 * Lookup order:
 *  1. `enums.<enumName>.<value>`
 *  2. `enums.<enumName>.<fallbackName without spaces/punctuation>` (API types whose id is numeric)
 *  3. the fallback name as given, or the value with spaces added ("RequestedAtRegistry" -> "Requested At Registry")
 */
@Injectable({ providedIn: 'root' })
export class EnumLabelService {
  private readonly transloco = inject(TranslocoService);

  constructor() {
    // Lets static helpers (Utilities.getPartyTypeString etc.) translate without DI
    Utilities.enumLabel = (enumName, value, fallbackName) => this.label(enumName, value, fallbackName);
  }

  label(enumName: string, value: string | number | boolean | null | undefined, fallbackName?: string | null): string {
    if (value === null || value === undefined || value === '') {
      return fallbackName ?? '';
    }

    const numeric = NUMERIC_ENUMS[enumName];
    if (numeric && (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value)))) {
      const memberName = numeric[Number(value)];
      if (typeof memberName === 'string') {
        value = memberName;
      }
    }

    const byValue = this.lookup(enumName, String(value));
    if (byValue !== null) {
      return byValue;
    }

    if (fallbackName) {
      const byName = this.lookup(enumName, fallbackName.replace(/[^A-Za-z0-9_]/g, ''));
      return byName ?? fallbackName;
    }

    return typeof value === 'string' ? Utilities.spacePascalCase(value) : String(value);
  }

  /**
   * Translates a jurisdiction code using `jurisdictions.<country>.<code>`, e.g. ('BC', 'CA') -> "Colombie-Britannique".
   * Without a country (or no match for it) Canada then the US are tried, so codes like _F / _M still resolve.
   * Falls back to the API name (DisplayJurisdiction / JurisdictionFullName), then the code.
   */
  jurisdiction(code: string | null | undefined, country?: string | null, fallbackName?: string | null): string {
    if (!code) {
      return fallbackName ?? '';
    }

    const countries = [country, 'CA', 'US'].filter((c, i, all): c is string => !!c && all.indexOf(c) === i);
    for (const c of countries) {
      const text = this.translateKey(`jurisdictions.${c}.${code}`);
      if (text !== null) {
        return text;
      }
    }

    return fallbackName || code;
  }

  /** Translates a country code using `countries.<code>`, falling back to the API name, then the code. */
  country(code: string | null | undefined, fallbackName?: string | null): string {
    if (!code) {
      return fallbackName ?? '';
    }
    return this.translateKey(`countries.${code}`) ?? (fallbackName || code);
  }

  /**
   * Translates display text the API sends as English (UI configuration titles such as "General Collateral")
   * using `uiConfigurationText.<text without spaces/punctuation>`, e.g. "Link To" -> uiConfigurationText.LinkTo.
   * Text without a translation is returned as given.
   */
  apiText(text: string | null | undefined): string {
    if (!text) {
      return text ?? '';
    }
    const key = text.replace(/[^A-Za-z0-9_]/g, '');
    return (key && this.translateKey(`uiConfigurationText.${key}`)) || text;
  }

  /** True when a translation exists for this value. */
  has(enumName: string, value: string | number | boolean | null | undefined): boolean {
    return value !== null && value !== undefined && this.lookup(enumName, String(value)) !== null;
  }

  private lookup(enumName: string, value: string): string | null {
    if (!value) {
      return null;
    }
    return this.translateKey(`enums.${enumName}.${value}`);
  }

  private translateKey(key: string): string | null {
    const text = this.transloco.translate(key);
    return text && text !== key ? text : null;
  }
}
